import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  Input,
} from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { LrsService, LrsRoute } from 'src/app/shared/services/lrs.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { DocumentUploadComponent } from '../document-upload/document-upload.component';
import { DocumentViewerComponent } from '../document-viewer/document-viewer.component';
import { Observable, forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, tap, takeUntil } from 'rxjs/operators';

export interface DocumentPermissionRow {
  id?: number;
  role_id?: number | null;
  user_id?: number | null;
  permission_type: string;
  role_name?: string;
  user_name?: string;
}

export interface Document {
  id: number;
  document_name: string;
  description: string;
  file_name: string;
  file_size: number;
  file_type: string;
  file_extension: string;
  category_name?: string;
  uploaded_by_name?: string;
  created_at: string;
  updated_at: string;
  is_favorite: boolean;
  tags?: string[] | string;
  metadata?: Record<string, unknown> | string;
  status?: string;
  project_id?: number;
  permissions?: DocumentPermissionRow[] | null;
}

type DocumentStatusFilter = 'active' | 'deleted';

/** Quick-filter tags aligned with construction / highway document workflows */
const QUICK_TAG_FILTERS = [
  'Confidential',
  'Drone',
  'Survey',
  'Earthwork',
  'Inspection',
] as const;

interface PermissionEditorRow {
  role_id: number | null;
  permission_type: string;
}

const PERMISSION_TYPES = ['view', 'download', 'edit', 'delete'] as const;

@Component({
  selector: 'app-document-list',
  templateUrl: './document-list.component.html',
  styleUrls: ['./document-list.component.css']
})
export class DocumentListComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  Math = Math;

  displayedColumns: string[] = [
    'select',
    'id',
    'document_name',
    'lrs_corridor',
    'category',
    'file_type',
    'file_size',
    'updated_at',
    'uploaded_by',
    'actions',
  ];

  dataSource = new MatTableDataSource<Document>([]);
  selection = new SelectionModel<Document>(true, []);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;
  bulkActionInProgress = false;
  detailLoading = false;

  searchKeyword = '';
  selectedCategory = '';
  selectedProject = '';
  statusFilter: DocumentStatusFilter = 'active';
  sortBy = 'created_at';
  sortOrder: 'ASC' | 'DESC' = 'DESC';

  categories: any[] = [];
  projects: any[] = [];
  orgRoles: Array<{ role_id: number; role_name: string }> = [];
  orgDocumentTags: string[] = [];

  readonly quickTagFilters = [...QUICK_TAG_FILTERS];
  readonly permissionTypes = [...PERMISSION_TYPES];

  activeTagFilter: string | null = null;
  filterSharedOnly = false;
  filterFavoritesOnly = false;

  selectedDocument: Document | null = null;
  documentDetail: Document | null = null;

  /** Editable copy of tags in the sidebar */
  editableTags: string[] = [];
  sidebarTagInput = '';
  tagsSaving = false;

  permissionsPanelOpen = false;
  permissionRows: PermissionEditorRow[] = [];
  orgWideAccess = true;
  permissionsSaving = false;

  lrsRoutes: LrsRoute[] = [];
  lrsRouteId: number | null = null;
  lrsFromKm: number | null = null;
  lrsToKm: number | null = null;
  lrsMeasureKm: number | null = null;
  lrsChainageLabel = '';
  lrsSaving = false;

  @Input() embeddedProjectId: string | number | null = null;

  routeProjectId: string | null = null;

  get embeddedInProject(): boolean {
    return this.embeddedProjectId != null && String(this.embeddedProjectId).trim() !== '';
  }

  get selectedCount(): number {
    return this.selection.selected.length;
  }

  get isDeletedView(): boolean {
    return this.statusFilter === 'deleted';
  }

  get sortLabel(): string {
    return this.sortBy === 'document_name' && this.sortOrder === 'ASC'
      ? 'Name A–Z'
      : this.sortBy === 'document_name'
        ? 'Name Z–A'
        : 'Newest first';
  }

  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 20, 50];

  private readonly destroy$ = new Subject<void>();
  private bootstrapSub: Subscription | null = null;

  constructor(
    private apiService: ApiService,
    private lrsService: LrsService,
    private toastrService: ToastrMsgService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadCategories();
    this.loadOrgRoles();
    this.loadLrsRoutes();
    this.bootstrapProjectsAndRoute();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['embeddedProjectId'] || changes['embeddedProjectId'].firstChange) {
      return;
    }
    if (!this.embeddedInProject) {
      return;
    }
    this.pageIndex = 0;
    this.bootstrapProjectsAndRoute();
  }

  ngOnDestroy(): void {
    this.bootstrapSub?.unsubscribe();
    this.bootstrapSub = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  private bootstrapProjectsAndRoute(): void {
    this.bootstrapSub?.unsubscribe();
    this.bootstrapSub = this.apiService.getProjectListForDropdown({ limit: 300 }).pipe(
      map((res: any) => (res.success ? res.data || [] : [])),
      catchError(() => of([])),
      tap((projects: any[]) => {
        this.projects = projects;
      }),
      switchMap(() => {
        if (this.embeddedInProject) {
          return this.resolveLockedProjectId(String(this.embeddedProjectId));
        }
        return this.route.paramMap.pipe(
          switchMap((pm) => this.applyRouteProjectId(pm))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageIndex = 0;
      this.loadDocuments();
    });
  }

  private applyRouteProjectId(pm: ParamMap): Observable<void> {
    return this.resolveLockedProjectId(pm.get('id'));
  }

  private resolveLockedProjectId(rawId: string | null): Observable<void> {
    this.routeProjectId = rawId && /^\d+$/.test(rawId) ? rawId : null;
    if (rawId && /^\d+$/.test(rawId)) {
      const idStr = rawId;
      const exists = this.projects.some((p: any) => String(p.id) === idStr);
      if (exists) {
        this.selectedProject = idStr;
        return of(undefined);
      }
      return this.apiService.getProjectCommonId(idStr).pipe(
        tap((proj: any) => {
          if (proj?.id != null && proj?.project_name) {
            this.projects = [...this.projects, { id: proj.id, project_name: proj.project_name }].sort(
              (a: any, b: any) => (a.project_name || '').localeCompare(b.project_name || '')
            );
          }
          this.selectedProject = idStr;
        }),
        catchError(() => {
          this.selectedProject = idStr;
          return of(undefined);
        }),
        map((): undefined => undefined)
      );
    }
    this.selectedProject = '';
    return of(undefined);
  }

  onProjectChange(projectId: string): void {
    if (this.embeddedInProject) {
      return;
    }
    if (projectId) {
      void this.router.navigate(['/home/document-list', projectId]);
    } else {
      void this.router.navigate(['/home/document-list']);
    }
  }

  loadDocuments() {
    this.loading = true;
    this.selection.clear();

    const params: Record<string, string | number> = {
      page: this.pageIndex + 1,
      limit: this.pageSize,
      status: this.statusFilter,
      sort_by: this.sortBy,
      sort_order: this.sortOrder,
    };

    if (this.searchKeyword) {
      params['search'] = this.searchKeyword;
    }
    if (this.selectedCategory) {
      params['category_id'] = this.selectedCategory;
    }
    if (this.selectedProject) {
      params['project_id'] = this.selectedProject;
    }
    if (this.activeTagFilter) {
      params['tag'] = this.activeTagFilter;
    }
    if (this.filterSharedOnly) {
      params['shared_only'] = 'true';
    }
    if (this.filterFavoritesOnly) {
      params['favorite_only'] = 'true';
    }

    this.apiService.getDocuments(params).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.dataSource.data = res.data;
          this.totalItems = res.pagination?.total || 0;
          this.syncSelectionAfterLoad();
        } else {
          this.toastrService.error('Failed to load documents');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading documents:', err);
        this.toastrService.error(err.error?.error || 'Failed to load documents');
        this.loading = false;
      }
    });

    this.loadOrgDocumentTags();
  }

  loadOrgRoles(): void {
    this.apiService.getAllRoles().subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res) ? res : res?.data || [];
        this.orgRoles = rows
          .map((r: any) => ({
            role_id: Number(r.role_id ?? r.id),
            role_name: r.role_name || `Role #${r.role_id ?? r.id}`,
          }))
          .filter((r: { role_id: number }) => Number.isFinite(r.role_id));
      },
      error: () => {
        this.orgRoles = [];
      },
    });
  }

  loadOrgDocumentTags(): void {
    this.apiService.getDocumentTags({ status: this.statusFilter }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.orgDocumentTags = res.data || [];
        }
      },
      error: () => {
        this.orgDocumentTags = [];
      },
    });
  }

  get tagFilterChips(): string[] {
    const merged = new Set<string>([...this.quickTagFilters, ...this.orgDocumentTags]);
    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  }

  toggleTagFilter(tag: string): void {
    this.activeTagFilter = this.activeTagFilter === tag ? null : tag;
    this.pageIndex = 0;
    this.loadDocuments();
  }

  toggleSharedFilter(): void {
    this.filterSharedOnly = !this.filterSharedOnly;
    if (this.filterSharedOnly) {
      this.filterFavoritesOnly = false;
    }
    this.pageIndex = 0;
    this.loadDocuments();
  }

  toggleFavoritesFilter(): void {
    this.filterFavoritesOnly = !this.filterFavoritesOnly;
    if (this.filterFavoritesOnly) {
      this.filterSharedOnly = false;
    }
    this.pageIndex = 0;
    this.loadDocuments();
  }

  isDocumentShared(doc: Document | null): boolean {
    if (!doc?.permissions) {
      return false;
    }
    const perms = Array.isArray(doc.permissions) ? doc.permissions : [];
    return perms.length > 0;
  }

  hasDocumentTag(doc: Document | null, tag: string): boolean {
    return this.getDocumentTags(doc).some((t) => t.toLowerCase() === tag.toLowerCase());
  }

  private syncSelectionAfterLoad(): void {
    if (!this.selectedDocument) {
      return;
    }
    const match = this.dataSource.data.find((d) => d.id === this.selectedDocument!.id);
    if (match) {
      this.selectedDocument = match;
      this.documentDetail = match;
    } else {
      this.selectedDocument = null;
      this.documentDetail = null;
    }
  }

  loadCategories() {
    this.apiService.getDocumentCategories().subscribe({
      next: (res: any) => {
        if (res.success) {
          this.categories = res.data;
        }
      },
      error: (err: any) => {
        console.error('Error loading categories:', err);
      }
    });
  }

  applyFilterFromSearch(event: Event) {
    this.searchKeyword = (event.target as HTMLInputElement).value.trim();
    this.pageIndex = 0;
    this.loadDocuments();
  }

  onCategoryFilterChange() {
    this.pageIndex = 0;
    this.loadDocuments();
  }

  setStatusFilter(status: DocumentStatusFilter) {
    if (this.statusFilter === status) {
      return;
    }
    this.statusFilter = status;
    this.pageIndex = 0;
    this.activeTagFilter = null;
    this.filterSharedOnly = false;
    this.filterFavoritesOnly = false;
    this.clearDetailSelection();
    this.loadDocuments();
  }

  toggleNameSort() {
    if (this.sortBy === 'document_name') {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortBy = 'document_name';
      this.sortOrder = 'ASC';
    }
    this.pageIndex = 0;
    this.loadDocuments();
  }

  setNewestSort() {
    this.sortBy = 'created_at';
    this.sortOrder = 'DESC';
    this.pageIndex = 0;
    this.loadDocuments();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadDocuments();
  }

  /** Whether the number of selected elements matches the total number of rows on this page. */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numRows > 0 && numSelected === numRows;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach((row) => this.selection.select(row));
    }
  }

  onRowClick(document: Document, event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('button, a, mat-checkbox, input, .mat-mdc-checkbox')) {
      return;
    }
    this.selectDocument(document);
  }

  selectDocument(document: Document): void {
    this.selectedDocument = document;
    this.documentDetail = document;
    this.editableTags = [...this.getDocumentTags(document)];
    this.sidebarTagInput = '';
    this.permissionsPanelOpen = false;
    this.hydrateLrsFromDocument(document);

    if (this.isDeletedView) {
      return;
    }

    this.detailLoading = true;
    this.apiService.getDocumentById(document.id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.documentDetail = { ...document, ...res.data };
          this.editableTags = [...this.getDocumentTags(this.documentDetail)];
          this.initPermissionsEditor(this.documentDetail);
          this.hydrateLrsFromDocument(this.documentDetail);
        }
        this.detailLoading = false;
      },
      error: () => {
        this.detailLoading = false;
        this.initPermissionsEditor(document);
      }
    });
  }

  clearDetailSelection(): void {
    this.selectedDocument = null;
    this.documentDetail = null;
    this.editableTags = [];
    this.permissionRows = [];
    this.orgWideAccess = true;
    this.permissionsPanelOpen = false;
  }

  initPermissionsEditor(doc: Document): void {
    const perms = doc.permissions;
    const rows = Array.isArray(perms) ? perms.filter((p) => p && (p.role_id || p.user_id)) : [];
    this.orgWideAccess = rows.length === 0;
    this.permissionRows = rows
      .filter((p) => p.role_id)
      .map((p) => ({
        role_id: p.role_id != null ? Number(p.role_id) : null,
        permission_type: p.permission_type || 'view',
      }));
    if (!this.orgWideAccess && this.permissionRows.length === 0 && rows.length > 0) {
      this.permissionRows = [{ role_id: null, permission_type: 'view' }];
      this.orgWideAccess = false;
    }
  }

  togglePermissionsPanel(): void {
    this.permissionsPanelOpen = !this.permissionsPanelOpen;
    if (this.permissionsPanelOpen && this.documentDetail) {
      this.initPermissionsEditor(this.documentDetail);
    }
  }

  setOrgWideAccess(enabled: boolean): void {
    this.orgWideAccess = enabled;
    if (enabled) {
      this.permissionRows = [];
    } else if (this.permissionRows.length === 0) {
      this.addPermissionRow();
    }
  }

  addPermissionRow(): void {
    this.orgWideAccess = false;
    this.permissionRows = [
      ...this.permissionRows,
      { role_id: this.orgRoles[0]?.role_id ?? null, permission_type: 'view' },
    ];
  }

  removePermissionRow(index: number): void {
    this.permissionRows = this.permissionRows.filter((_, i) => i !== index);
    if (this.permissionRows.length === 0) {
      this.orgWideAccess = true;
    }
  }

  getRoleName(roleId: number | null | undefined): string {
    if (roleId == null) {
      return '—';
    }
    const row = this.orgRoles.find((r) => r.role_id === roleId);
    return row?.role_name || `Role #${roleId}`;
  }

  getPermissionSummary(doc: Document | null): string {
    if (!doc) {
      return '';
    }
    if (this.isDocumentShared(doc)) {
      const count = (doc.permissions || []).length;
      return `Shared with ${count} rule${count === 1 ? '' : 's'}`;
    }
    return 'Organization-wide (default)';
  }

  savePermissions(): void {
    if (!this.selectedDocument || this.isDeletedView) {
      return;
    }

    if (!this.orgWideAccess) {
      const invalid = this.permissionRows.some((r) => r.role_id == null);
      if (invalid) {
        this.toastrService.error('Select a role for each permission row');
        return;
      }
    }

    const payload = this.orgWideAccess
      ? []
      : this.permissionRows.map((r) => ({
          role_id: r.role_id,
          permission_type: r.permission_type,
        }));

    this.permissionsSaving = true;
    this.apiService.setDocumentPermissions(this.selectedDocument.id, payload).subscribe({
      next: (res: any) => {
        this.permissionsSaving = false;
        if (res.success) {
          this.toastrService.success('Permissions updated');
          this.reloadSelectedDocumentDetail();
          this.loadDocuments();
        }
      },
      error: (err: any) => {
        this.permissionsSaving = false;
        this.toastrService.error(err.error?.error || 'Failed to update permissions');
      },
    });
  }

  addSidebarTag(): void {
    const tag = this.sidebarTagInput.trim();
    if (!tag) {
      return;
    }
    if (!this.editableTags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      this.editableTags = [...this.editableTags, tag];
    }
    this.sidebarTagInput = '';
  }

  quickAddSidebarTag(tag: string): void {
    if (!this.editableTags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      this.editableTags = [...this.editableTags, tag];
    }
  }

  removeEditableTag(tag: string): void {
    this.editableTags = this.editableTags.filter((t) => t !== tag);
  }

  saveTags(): void {
    if (!this.selectedDocument || this.isDeletedView) {
      return;
    }
    this.tagsSaving = true;
    this.apiService.updateDocument(this.selectedDocument.id, { tags: this.editableTags }).subscribe({
      next: (res: any) => {
        this.tagsSaving = false;
        if (res.success) {
          this.toastrService.success('Tags updated');
          if (this.documentDetail) {
            this.documentDetail.tags = [...this.editableTags];
          }
          if (this.selectedDocument) {
            this.selectedDocument.tags = [...this.editableTags];
          }
          this.loadDocuments();
          this.loadOrgDocumentTags();
        }
      },
      error: (err: any) => {
        this.tagsSaving = false;
        this.toastrService.error(err.error?.error || 'Failed to update tags');
      },
    });
  }

  private reloadSelectedDocumentDetail(): void {
    if (!this.selectedDocument || this.isDeletedView) {
      return;
    }
    const id = this.selectedDocument.id;
    this.apiService.getDocumentById(id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.documentDetail = { ...this.selectedDocument!, ...res.data };
          this.initPermissionsEditor(this.documentDetail);
        }
      },
    });
  }

  isRowActive(document: Document): boolean {
    return this.selectedDocument?.id === document.id;
  }

  getProjectDisplayName(): string {
    if (!this.selectedProject) {
      return 'All projects';
    }
    const row = this.projects.find((p: any) => String(p.id) === String(this.selectedProject));
    return row?.project_name || `Project #${this.selectedProject}`;
  }

  getDocumentTags(doc: Document | null): string[] {
    if (!doc?.tags) {
      return [];
    }
    if (Array.isArray(doc.tags)) {
      return doc.tags.filter((t) => !!t);
    }
    if (typeof doc.tags === 'string') {
      try {
        const parsed = JSON.parse(doc.tags);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  getDocumentMetadata(doc: Document | null): Record<string, unknown> {
    if (!doc?.metadata) {
      return {};
    }
    if (typeof doc.metadata === 'object' && !Array.isArray(doc.metadata)) {
      return doc.metadata as Record<string, unknown>;
    }
    if (typeof doc.metadata === 'string') {
      try {
        const parsed = JSON.parse(doc.metadata);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
      } catch {
        return {};
      }
    }
    return {};
  }

  getDocumentLrs(doc: Document | null): Record<string, unknown> {
    const meta = this.getDocumentMetadata(doc);
    const lrs = meta['lrs'];
    return typeof lrs === 'object' && lrs !== null ? (lrs as Record<string, unknown>) : {};
  }

  loadLrsRoutes(): void {
    const projectId = this.selectedProject || this.routeProjectId || undefined;
    this.lrsService.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.lrsRoutes = res.data || [];
      },
      error: () => {
        this.lrsRoutes = [];
      },
    });
  }

  private hydrateLrsFromDocument(doc: Document | null): void {
    const lrs = this.getDocumentLrs(doc);
    this.lrsRouteId = lrs['route_id'] != null ? Number(lrs['route_id']) : null;
    this.lrsFromKm = lrs['from_km'] != null ? Number(lrs['from_km']) : null;
    this.lrsToKm = lrs['to_km'] != null ? Number(lrs['to_km']) : null;
    this.lrsMeasureKm = lrs['measure_km'] != null ? Number(lrs['measure_km']) : null;
    this.lrsChainageLabel = String(lrs['chainage'] || lrs['reference'] || '');
  }

  saveDocumentLrs(): void {
    if (!this.selectedDocument || this.isDeletedView || !this.lrsRouteId) {
      this.toastrService.error('Select a route before saving LRS location');
      return;
    }
    this.lrsSaving = true;
    const lrsPayload = {
      route_id: this.lrsRouteId,
      measure_km: this.lrsMeasureKm ?? undefined,
      chainage: this.lrsChainageLabel || undefined,
      from_km: this.lrsFromKm ?? undefined,
      to_km: this.lrsToKm ?? undefined,
    };
    this.lrsService.attachDocumentLrs(this.selectedDocument.id, lrsPayload).subscribe({
      next: () => {
        const meta = {
          ...this.getDocumentMetadata(this.documentDetail || this.selectedDocument),
          lrs: {
            route_id: this.lrsRouteId,
            from_km: this.lrsFromKm,
            to_km: this.lrsToKm,
            measure_km: this.lrsMeasureKm,
            chainage: this.lrsChainageLabel || undefined,
          },
        };
        this.apiService
          .updateDocument(this.selectedDocument!.id, { metadata: meta })
          .subscribe({
            next: () => {
              this.lrsSaving = false;
              this.toastrService.success('LRS chainage linked to document');
              this.reloadSelectedDocumentDetail();
            },
            error: () => {
              this.lrsSaving = false;
              this.toastrService.success('LRS link saved (metadata sync pending)');
              this.reloadSelectedDocumentDetail();
            },
          });
      },
      error: (err) => {
        this.lrsSaving = false;
        this.toastrService.error(err?.error?.error || 'Failed to attach LRS location');
      },
    });
  }

  formatLrsCorridorLabel(doc: Document | null): string {
    const lrs = this.getDocumentLrs(doc);
    if (!lrs['route_id']) return '—';
    const parts: string[] = [`Route #${lrs['route_id']}`];
    if (lrs['chainage']) parts.push(String(lrs['chainage']));
    else if (lrs['from_km'] != null && lrs['to_km'] != null) {
      parts.push(`${lrs['from_km']}–${lrs['to_km']} km`);
    } else if (lrs['measure_km'] != null) {
      parts.push(`km ${lrs['measure_km']}`);
    }
    return parts.join(' · ');
  }

  openDocumentOnMap(): void {
    if (!this.selectedDocument) return;
    const queryParams: Record<string, string | number> = {
      document_id: this.selectedDocument.id,
      tab: 'lrs',
    };
    if (this.lrsRouteId) queryParams['route_id'] = this.lrsRouteId;
    if (this.lrsFromKm != null) queryParams['from_km'] = this.lrsFromKm;
    if (this.lrsToKm != null) queryParams['to_km'] = this.lrsToKm;
    if (this.selectedProject) queryParams['project_id'] = this.selectedProject;
    void this.router.navigate(['/home/geospatial'], { queryParams });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  getFileIcon(fileType: string): string {
    if (fileType?.includes('pdf')) return 'picture_as_pdf';
    if (fileType?.includes('word') || fileType?.includes('document')) return 'description';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return 'table_chart';
    if (fileType?.includes('image')) return 'image';
    if (fileType?.includes('video')) return 'videocam';
    if (fileType?.includes('zip') || fileType?.includes('rar')) return 'folder_zip';
    return 'insert_drive_file';
  }

  openUploadDialog() {
    if (this.isDeletedView) {
      this.toastrService.error('Switch to Active documents to upload');
      return;
    }

    const pidRaw = this.selectedProject ? Number(this.selectedProject) : null;
    const projectId =
      pidRaw != null && Number.isFinite(pidRaw) && pidRaw > 0 ? pidRaw : null;
    let projectName: string | null = null;
    if (projectId != null) {
      const row = this.projects.find((p: any) => Number(p.id) === projectId);
      projectName = row?.project_name ?? null;
    }
    const dialogRef = this.dialog.open(DocumentUploadComponent, {
      width: '600px',
      data: {
        categories: this.categories,
        projectId,
        projectName,
        projects: [...this.projects],
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadDocuments();
      }
    });
  }

  viewDocument(document: Document) {
    this.dialog.open(DocumentViewerComponent, {
      width: '90%',
      maxWidth: '1200px',
      data: { document }
    });
  }

  downloadDocument(doc: Document) {
    this.apiService.downloadDocument(doc.id).subscribe({
      next: (blob: Blob) => {
        this.triggerFileDownload(blob, doc.file_name);
        this.toastrService.success('Document downloaded successfully');
      },
      error: (err: any) => {
        console.error('Error downloading document:', err);
        this.toastrService.error(err.error?.error || 'Failed to download document');
      }
    });
  }

  private triggerFileDownload(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  bulkDownload(): void {
    const docs = this.selection.selected;
    if (!docs.length) {
      return;
    }
    this.bulkActionInProgress = true;
    let index = 0;

    const downloadNext = () => {
      if (index >= docs.length) {
        this.bulkActionInProgress = false;
        this.toastrService.success(`Downloaded ${docs.length} document(s)`);
        return;
      }
      const doc = docs[index++];
      this.apiService.downloadDocument(doc.id).subscribe({
        next: (blob: Blob) => {
          this.triggerFileDownload(blob, doc.file_name);
          setTimeout(downloadNext, 400);
        },
        error: () => {
          this.toastrService.error(`Failed to download "${doc.document_name}"`);
          setTimeout(downloadNext, 400);
        }
      });
    };

    downloadNext();
  }

  deleteDocument(document: Document) {
    if (!confirm(`Are you sure you want to delete "${document.document_name}"?`)) {
      return;
    }
    this.apiService.deleteDocument(document.id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Document deleted successfully');
          if (this.selectedDocument?.id === document.id) {
            this.clearDetailSelection();
          }
          this.loadDocuments();
        }
      },
      error: (err: any) => {
        console.error('Error deleting document:', err);
        this.toastrService.error(err.error?.error || 'Failed to delete document');
      }
    });
  }

  bulkDelete(): void {
    const docs = this.selection.selected;
    if (!docs.length) {
      return;
    }
    if (!confirm(`Delete ${docs.length} selected document(s)?`)) {
      return;
    }

    this.bulkActionInProgress = true;
    forkJoin(
      docs.map((doc) =>
        this.apiService.deleteDocument(doc.id).pipe(
          catchError(() => of({ success: false, id: doc.id }))
        )
      )
    )
      .pipe(finalize(() => (this.bulkActionInProgress = false)))
      .subscribe((results: any[]) => {
        const ok = results.filter((r) => r?.success).length;
        const failed = docs.length - ok;
        if (ok) {
          this.toastrService.success(`Deleted ${ok} document(s)`);
        }
        if (failed) {
          this.toastrService.error(`${failed} document(s) could not be deleted`);
        }
        this.clearDetailSelection();
        this.loadDocuments();
      });
  }

  restoreDocument(document: Document): void {
    this.apiService.restoreDocument(document.id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Document restored');
          if (this.selectedDocument?.id === document.id) {
            this.clearDetailSelection();
          }
          this.loadDocuments();
        }
      },
      error: (err: any) => {
        this.toastrService.error(err.error?.error || 'Failed to restore document');
      }
    });
  }

  bulkRestore(): void {
    const docs = this.selection.selected;
    if (!docs.length) {
      return;
    }

    this.bulkActionInProgress = true;
    forkJoin(
      docs.map((doc) =>
        this.apiService.restoreDocument(doc.id).pipe(
          catchError(() => of({ success: false }))
        )
      )
    )
      .pipe(finalize(() => (this.bulkActionInProgress = false)))
      .subscribe((results: any[]) => {
        const ok = results.filter((r) => r?.success).length;
        if (ok) {
          this.toastrService.success(`Restored ${ok} document(s)`);
        }
        if (ok < docs.length) {
          this.toastrService.error(`${docs.length - ok} document(s) could not be restored`);
        }
        this.clearDetailSelection();
        this.loadDocuments();
      });
  }

  toggleFavorite(document: Document) {
    if (this.isDeletedView) {
      return;
    }
    this.apiService.toggleDocumentFavorite(document.id).subscribe({
      next: (res: any) => {
        if (res.success) {
          document.is_favorite = res.is_favorite;
          if (this.documentDetail?.id === document.id) {
            this.documentDetail.is_favorite = res.is_favorite;
          }
          this.toastrService.success(res.message);
        }
      },
      error: (err: any) => {
        console.error('Error toggling favorite:', err);
        this.toastrService.error(err.error?.error || 'Failed to update favorite');
      }
    });
  }

  clearFilters() {
    this.searchKeyword = '';
    this.selectedCategory = '';
    this.statusFilter = 'active';
    this.activeTagFilter = null;
    this.filterSharedOnly = false;
    this.filterFavoritesOnly = false;
    this.sortBy = 'created_at';
    this.sortOrder = 'DESC';
    this.pageIndex = 0;
    const routeId = this.route.snapshot.paramMap.get('id');
    const onDocumentList = /\/home\/document-list(\/|$)/.test(this.router.url);

    if (this.embeddedInProject) {
      this.clearDetailSelection();
      this.loadDocuments();
      return;
    }

    if (routeId && onDocumentList) {
      void this.router.navigate(['/home/document-list']);
      return;
    }

    if (!routeId) {
      this.selectedProject = '';
    }
    this.clearDetailSelection();
    this.loadDocuments();
  }
}
